import React, {useState} from 'react'
import {Block, Col, Row} from 'jsxstyle'
import i18n from '../../../../common/i18n'
import {XpertKnowledgeTutorialDto, XpertKnowledgeTutorialStatus} from '../../../../services/XpertKnowledgeTutorial/type'
import {useXpertKnowledgeAuthContext} from '../../../../components/AuthContext/XpertKnowledgeAuthContext'
import {statusConfig} from '../../../XpertKnowledge/components/XpertKnowledgeUnPlublishedBlock'
import ActivityLogsDialog from '../../../../components/ActivityLogsDialog/ActivityLogsDialog'
import {UserRole} from '../../../../services/User/type'
import {useXpertKnowledgeTutorialContext} from '../../XpertKnowledgeTutorialContext'
import RichTextContent from '../../../../components/RichTextContent'
import Loader from '../../../../components/Loader'

export interface XpertKnowledgeActivityLogsProps {
  xpertKnowledgeTutorial: XpertKnowledgeTutorialDto
  onSubmit?: () => void
}

const XpertKnowledgeActivityLogs: React.FC<XpertKnowledgeActivityLogsProps> = ({xpertKnowledgeTutorial, onSubmit}) => {
  const {user} = useXpertKnowledgeAuthContext()
  const {approveXpertKnowledgeTutorial, requestChangeXpertKnowledgeTutorial, dismissRequestChangeXpertKnowledgeTutorial} =
    useXpertKnowledgeTutorialContext()
  const [isChangeRequestDialogOpen, setIsChangeRequestDialogOpen] = useState(false)
  const [isDismissing, setIsDismissing] = useState(false)

  const mostRecentLog = xpertKnowledgeTutorial.activityLogs?.length
    ? xpertKnowledgeTutorial.activityLogs.reduce((latest, current) =>
        new Date(current.createdAt).getTime() > new Date(latest.createdAt).getTime() ? current : latest
      )
    : null

  const xkStatus = xpertKnowledgeTutorial.status
  const cfg = statusConfig[xkStatus]

  const handleDismissRequestChange = async () => {
    setIsDismissing(true)
    try {
      await dismissRequestChangeXpertKnowledgeTutorial()
    } finally {
      setIsDismissing(false)
    }
  }

  return (
    <>
      <Col padding="24px" borderRadius="12px" gap="12px" backgroundColor="#FFFFFF" border="1px solid #E0E0E0">
        <Col gap="8px">
          <Block fontWeight={400} fontSize="12px" lineHeight="21px" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis" color="#000000">
            {i18n.t('xk.tutorial.activityLog.statusLabel')}
          </Block>

          <Row
            alignItems="center"
            justifyContent="center"
            padding="6px 14px"
            fontSize="12px"
            width="fit-content"
            height="30px"
            fontWeight={300}
            borderRadius="50px"
            backgroundColor={cfg.backgroundColor}
            color={cfg.color}
          >
            {cfg.label}
          </Row>
        </Col>
        {user?.role === UserRole.ADMIN_SIGHTCALL && (
          <Col gap="8px">
            <Block fontWeight={400} fontSize="12px" lineHeight="21px" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis" color="#000000">
              {i18n.t('xk.tutorial.activityLog.generationType')}
            </Block>

            <Row
              alignItems="center"
              justifyContent="center"
              padding="6px 14px"
              fontSize="12px"
              width="fit-content"
              height="30px"
              fontWeight={300}
              borderRadius="50px"
              backgroundColor="#F3F4F6"
              color="#6B7280"
            >
              {xpertKnowledgeTutorial.generationType === 'WITH_DOCS'
                ? i18n.t('xk.tutorial.activityLog.generationTypeWithDocs')
                : i18n.t('xk.tutorial.activityLog.generationTypeClassic')}
            </Row>
          </Col>
        )}
        {xkStatus !== XpertKnowledgeTutorialStatus.PUBLISHED && (
          <Col gap="8px">
            <Block fontWeight={400} fontSize="12px" lineHeight="21px" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis" color="#000000">
              {i18n.t('xk.tutorial.activityLog.commentLabel')}
            </Block>

            <Col padding="12px 16px" gap="8px" borderRadius="12px" backgroundColor="#F7F8F9">
              {xkStatus === XpertKnowledgeTutorialStatus.DRAFT ? (
                <Block fontWeight={300} fontSize="12px" lineHeight="21px" color="#666666" whiteSpace="pre-wrap">
                  <RichTextContent content={i18n.t('xk.tutorial.activityLog.empty')} />
                </Block>
              ) : (
                <>
                  <Block fontWeight={500} fontSize="14px" lineHeight="21px" color="#333333" props={{'data-attr-masked': 'true'} as any}>
                    {mostRecentLog?.userFullname}
                  </Block>
                  <Block
                    fontWeight={300}
                    fontSize="12px"
                    lineHeight="21px"
                    color="#666666"
                    whiteSpace="pre-wrap"
                    wordWrap="break-word"
                    wordBreak="break-word"
                    overflow="hidden"
                    width="100%"
                  >
                    <RichTextContent content={mostRecentLog?.comment ?? ''} />
                  </Block>
                </>
              )}
            </Col>
            {xkStatus === XpertKnowledgeTutorialStatus.DRAFT && user?.role == UserRole.AGENT && (
              <Row
                borderRadius="12px"
                backgroundColor="#7A42C2"
                marginTop="6px"
                hoverBackgroundColor="#7A22C2"
                hoverColor="#F7F8F9"
                fontWeight={500}
                color="#F7F8F9"
                fontSize="12px"
                padding="8px 16px"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                props={{
                  onClick: () => onSubmit && onSubmit(),
                }}
              >
                {i18n.t('xk.tutorial.activityLog.ask')}
              </Row>
            )}
            {xkStatus === XpertKnowledgeTutorialStatus.REFUSED && user?.role !== UserRole.AGENT && (
              <Row
                borderRadius="12px"
                backgroundColor={isDismissing ? '#CCCCCC' : '#7A42C2'}
                marginTop="6px"
                hoverBackgroundColor={isDismissing ? '#CCCCCC' : '#7A22C2'}
                hoverColor="#F7F8F9"
                fontWeight={500}
                color="#F7F8F9"
                fontSize="12px"
                padding="8px 16px"
                alignItems="center"
                justifyContent="center"
                cursor={isDismissing ? 'not-allowed' : 'pointer'}
                opacity={isDismissing ? 0.7 : 1}
                props={{
                  onClick: isDismissing ? undefined : handleDismissRequestChange,
                }}
              >
                {isDismissing ? (
                  <Loader size={16} borderWidth="2px" borderColor="#F7F8F9" borderTopColor="transparent" />
                ) : (
                  i18n.t('xk.tutorial.activityLog.rollback')
                )}
              </Row>
            )}
          </Col>
        )}

        {user?.role !== UserRole.AGENT && xkStatus === XpertKnowledgeTutorialStatus.IN_REVIEW && (
          <Row gap="10px">
            <Row
              width="50%"
              borderRadius="12px"
              border="1px solid #7A42C2"
              color="#7A42C2"
              fontWeight={500}
              fontSize="12px"
              padding="8px 16px"
              alignItems="center"
              backgroundColor="#FFFFFF"
              hoverBackgroundColor="#7A42C2"
              hoverColor="#F7F8F9"
              justifyContent="center"
              cursor="pointer"
              props={{onClick: () => setIsChangeRequestDialogOpen(true)}}
            >
              {i18n.t('xk.tutorial.activityLog.askChanges')}
            </Row>
            <Row
              width="50%"
              borderRadius="12px"
              backgroundColor="#7A42C2"
              hoverBackgroundColor="#7A22C2"
              hoverColor="#F7F8F9"
              fontWeight={500}
              color="#F7F8F9"
              fontSize="12px"
              padding="8px 16px"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              props={{onClick: () => approveXpertKnowledgeTutorial()}}
            >
              {i18n.t('xk.tutorial.activityLog.approvePublish')}
            </Row>
          </Row>
        )}
      </Col>

      <ActivityLogsDialog
        title={i18n.t('xk.tutorial.activityLog.dialog.askTitle')}
        subtitle={i18n.t('xk.tutorial.activityLog.dialog.askSubtitle')}
        sendLabel={i18n.t('xk.tutorial.activityLog.dialog.sendLabel')}
        placeholder={i18n.t('xk.tutorial.activityLog.dialog.placeholder')}
        onSend={(value) => {
          requestChangeXpertKnowledgeTutorial({comment: value})
          setIsChangeRequestDialogOpen(false)
        }}
        onCancel={() => setIsChangeRequestDialogOpen(false)}
        open={isChangeRequestDialogOpen}
        setOpen={setIsChangeRequestDialogOpen}
      />
    </>
  )
}

export default XpertKnowledgeActivityLogs
